import { createFileRoute, Outlet } from '@tanstack/react-router';
import { ArticlePath, createGuideLink, PageOrientation, TextKey } from '@mews-commander/enums';
import {
    GuideLinkButton,
    OptimusProvider,
    PageLayout,
    useBreadcrumbs,
    useEnterpriseBuildLocation,
    useTabs,
} from '@mews-commander/ui';
import { MewsTeam, RouteType } from '@mews/enums';
import { DateTimeUtils } from '@mews/framework';
import { PageSize } from '@mews-commander/data-access';
import { PrintAction } from '@mews-commander/modules/src/legacy/React/Components/HeadingAction/Action';
import HandleIconButton from '@mews-commander/modules/src/legacy/React/Components/HeadingAction/Handles/HandleIconButton';

export const Route = createFileRoute('/_authenticated/$currentEnterpriseId/Finance/Cashier/_layout/$cashierId')({
    component: () => <PageComponent />,
    loader: async ({
        params: { cashierId },
        context: {
            ensureQueryData,
            localizeText,
            globalization: { timezone, dateTimeLocalizationInfo },
        },
    }) => {
        const [shiftDetails, cashiers] = await Promise.all([
            ensureQueryData('cashiers', 'getCurrentShift', {
                cashierId,
            }),
            ensureQueryData('cashiers', 'getAll', {
                cashierIds: [cashierId],
            }),
        ]);

        const date = DateTimeUtils.dateTimeUtcStringToDateTimeString(
            shiftDetails.startUtc,
            dateTimeLocalizationInfo,
            timezone
        );

        const cashierName = cashiers.cashiers?.[0].name || '';

        return {
            title: () => localizeText({ textKey: TextKey.CashierShiftFromFull, substitutes: { cashierName, date } }),
        };
    },
    staticData: {
        owner: MewsTeam.Billing,
        type: RouteType.Frontend,
    },
});

const PageComponent = () => {
    const breadcrumbs = useBreadcrumbs();

    const { cashierId } = Route.useParams();

    const buildLocation = useEnterpriseBuildLocation();

    const printAction = {
        pageOrientation: PageOrientation.Portrait,
        pageSize: PageSize.A4,
        url: buildLocation({
            to: 'Cashier/Shift',
            search: {
                CashierId: cashierId,
                ForPrint: 'true',
            },
        }).href,
    };

    const actions = [
        <GuideLinkButton key="guide" guideLink={createGuideLink(ArticlePath.CreateDeleteCashier)} />,
        // @ts-expect-error $TSFixMeError: Type '{ action: { pageOrientation: PageOrientation; pageSize: PageSize | undefined; text: string | Element | undefined; ...
        <PrintAction key="print" action={printAction} component={HandleIconButton} />,
    ];

    const tabs = useTabs('/$currentEnterpriseId/Finance/Cashier/$cashierId', ['Detail', 'PastShifts']);

    return (
        <OptimusProvider density="spacious">
            <PageLayout padded actions={actions} tabs={tabs} breadcrumbs={breadcrumbs}>
                <Outlet />
            </PageLayout>
        </OptimusProvider>
    );
};
